import {
    ComponentTypes,
    IFieldVisibilityContext,
    isTemplateFieldVisible,
    ITemplate,
    ITemplateCheckboxComponent,
    ITemplateComponent,
} from "entities/template";
import { CdocFormat } from "../const/cdocFormat";
import { constructDocumentName } from "./filename";
import { IDocumentPayloadProps } from "../types/document_service_types";

const XXCDOC_AUTOCOMPLETE = "xxcdoc_autocomplete";

const applyHiddenAutocompleteToFormRecord = (
    formRecord: Record<string, any>,
    components: ITemplateComponent[] | undefined,
    visibilityContext: IFieldVisibilityContext | undefined,
): Record<string, any> => {
    if (!components || !visibilityContext || formRecord[XXCDOC_AUTOCOMPLETE] !== undefined) {
        return formRecord;
    }

    const autocompleteComponent = components.find(
        (c) => c.type === ComponentTypes.checkbox && "name" in c && c.name === XXCDOC_AUTOCOMPLETE,
    ) as ITemplateCheckboxComponent | undefined;

    if (!autocompleteComponent) {
        return formRecord;
    }

    if (isTemplateFieldVisible(autocompleteComponent, visibilityContext)) {
        return formRecord;
    }

    const defaultValue = typeof autocompleteComponent.defaultValue === "boolean"
        ? autocompleteComponent.defaultValue
        : true;

    return {
        ...formRecord,
        [XXCDOC_AUTOCOMPLETE]: String(defaultValue),
    };
};

export const constructDocumentPayload = (
    template: ITemplate,
    formRecord: Record<string, any>,
    xxya_cdoc_format: CdocFormat,
    xxya_cdoc_isnopass: boolean | undefined,
    xxcdoc_lang: string,
    extUserId: string | null = null,
    files?: File[],
    filesFieldName?: string,
    components?: ITemplateComponent[],
    visibilityContext?: IFieldVisibilityContext,
): IDocumentPayloadProps => {
    const fileName = constructDocumentName(template.name);
    const enrichedFormRecord = applyHiddenAutocompleteToFormRecord(formRecord, components, visibilityContext);
    const record: IDocumentPayloadProps = {
        xxya_cdoc_format,
        fileName,
        xxcdoc_lang,
        docId: template.id,
        groupId: template.group_id,
        docName: template.name,
        docCode: template.code,
        formRecord: enrichedFormRecord,
        files: files,
        filesFieldName,
    };

    if (extUserId) {
        record["extUserId"] = extUserId;
    }

    if (typeof xxya_cdoc_isnopass === "boolean") {
        record["xxya_cdoc_isnopass"] = xxya_cdoc_isnopass;
    }

    return record;
};

export const cleanFormRecord = (formRecord: Record<string, any>): Record<string, any> => {
    const cleanRecord: Record<string, any> = {};

    Object.keys(formRecord).forEach((key) => {
        if (formRecord[key] === undefined) {
            cleanRecord[key] = "";
        } else {
            cleanRecord[key] = formRecord[key];
        }
    });

    return cleanRecord;
};
